import Plotly from 'plotly.js-dist';
import Papa from 'papaparse';

console.log('hvhsvisualizer.js module loaded');

// HVHS Framework configuration - 10 components
const section_names = [
  'Digital Data Systems',
  'Analytics',
  'Cost measurement systems',
  'Outcome measurement systems',
  'Benchmarking',
  'Integrated Care Pathways',
  'Value-Based Payment',
  'Value-based procurement',
  'Integrated Provider Networks',
  'Strategic change and innovation ecosystem'
];

// Short labels for angular axis
const section_short_labels = ['C1', 'C2', 'C3', 'C4', 'C5', 'C6', 'C7', 'C8', 'C9', 'C10'];

// Shared score palette (ColorBrewer RdYlGn, low→high = red→green). Band edges are inclusive at the top:
// 1.0 falls in '1 – 2', 5.0 in '4 – 5'. Map, legend and page copy all derive from these.
const HVHS_SCORE_BANDS = [
  { min: 0, max: 1, label: '0 – 1', color: '#D73027' },
  { min: 1, max: 2, label: '1 – 2', color: '#FC8D59' },
  { min: 2, max: 3, label: '2 – 3', color: '#FEE08B' },
  { min: 3, max: 4, label: '3 – 4', color: '#91CF60' },
  { min: 4, max: 5, label: '4 – 5', color: '#1A9850' },
];
const HVHS_MISSING_COLOR = '#D1D5DB';
const HVHS_MISSING_LABEL = 'No data';

// Index of the band a score falls in (null for missing/non-numeric)
const scoreToBandIndex = (score) => {
  if (score === null || score === undefined || isNaN(score)) return null;
  if (score <= HVHS_SCORE_BANDS[0].min) return 0;
  const idx = HVHS_SCORE_BANDS.findIndex(b => score > b.min && score <= b.max);
  return idx === -1 ? HVHS_SCORE_BANDS.length - 1 : idx;
};

// Plotly stepped colorscale: each band colour repeated at both edges
const HVHS_BAND_COLORSCALE = HVHS_SCORE_BANDS.flatMap((b, i, arr) => [
  [i / arr.length, b.color],
  [(i + 1) / arr.length, b.color],
]);

// Component descriptions (from HVHS Dataset Protocol)
const COMPONENT_DESCRIPTIONS = {
  'Digital Data Systems': 'Digital infrastructure for standardization, data platforms, integrated datasets, interoperability, and data governance across the health system.',
  'Analytics': 'Advanced data science capabilities, compute infrastructure, and workforce for converting raw health data into actionable predictions and insights.',
  'Cost measurement systems': 'Harmonized cost framework, automated pooling, and cost accounting capabilities for measuring the full cost of care and illness.',
  'Outcome measurement systems': 'Harmonised outcomes framework, automation and pooling, and outcomes data capture for measuring health and patient experience outcomes.',
  'Benchmarking': 'Harmonised benchmarking framework, comparative analysis, and reporting for like-for-like comparisons of health system performance.',
  'Integrated Care Pathways': 'Harmonised care pathways, stratification and bundling, and care variation analysis for coordinated, person-centred care delivery.',
  'Value-Based Payment': 'Payment models including value-based remuneration and risk-reward alignment that reward value creation rather than volume.',
  'Value-based procurement': 'Procurement systems tying decisions to clinical outcomes, risk pooling mechanisms, and digital supply chain management.',
  'Integrated Provider Networks': 'Structural integration, operational integration, and information continuity across providers and system levels.',
  'Strategic change and innovation ecosystem': 'Strategic public-private partnerships, innovation ecosystem, and investment for driving health system transformation.'
};

// Element (sub-element) definitions — verbatim from the canonical framework doc
// "HVHS Definitions and Elements_1Jan2026_CR" (Che). Keyed by the EXACT element
// spelling used in the dataset so tooltips resolve. The bar chart is data-driven:
// only sub-elements that actually have a score are shown (Che, 2026-06: drop no-data
// sub-elements), so this table only needs the elements currently present in the data.
const ELEMENT_DEFINITIONS = {
  // C1 — Digital Data Systems
  'Data Governance and Stewardship': 'Securing data, protecting privacy, and establishing oversight rules and procedures.',
  'Digital Data Platforms': 'Capturing, reporting, and pooling clinical and wellness data via electronic records across the care continuum.',
  'Interoperability': 'Seamless data exchange between providers, patients, and payers.',
  // C2 — Analytics
  'Compute Infrastructure': 'Providing the data and high-performance compute, cloud regions and resilient networks required to run analytics at national scale.',
  'Workforce': 'Providing data scientists, ML engineers and domain experts (e.g., clinicians) who develop, validate and operationalise analytic models.',
  // C3 — Cost Measurement Systems
  'Automated Pooling': 'Automatically consolidating cost data from multiple providers into a national repository, enabling system-wide analysis and tariff setting.',
  'Cost Accounting Capabilities': 'Extracting, labelling and aggregating service-level costs into standardised unit-cost data that underpin efficiency analysis and payment design.',
  // C4 — Outcome measurement systems
  'Automation and Pooling': 'Automatically ingesting and pooling outcome data from provider systems into national repositories, enabling system-wide analysis and benchmarking.',
  'Outcomes Data Capture Capabilities': 'Collecting PROMs, PREMs and clinical outcomes electronically at the patient level within provider organisations.',
  // C5 — Benchmarking  (Harmonised Cost Framework currently labelled C5 in the data — see open question with Lisa/Che)
  'Comparative Analysis': 'Applying benchmarking metrics to compare value creation across system levels, public and private sectors, clinical domains, and political jurisdictions.',
  'Harmonised Benchmarking Framework': 'Establishing a standard metric set that enables like-for-like comparisons of health system outcomes, output objectives, outputs, functions and contextual factors, across public and private sectors.',
  'Harmonised Cost Framework': 'Providing all stakeholders with a shared set of definitions, indicators and methods to measure the full cost of care and illness.',
  'Reporting': 'Routinely publishing benchmarking results that inform policy and management decisions aimed at enhancing value creation at system level.',
  // C6 — Integrated care pathways
  'Harmonised Care Pathways': 'Embedding evidence-based, standardised clinical processes across providers and system levels, enabling consistency and preventing unwarranted deviation.',
  'Stratification and Bundling': 'Segmenting patients by risk or complexity and delivering coordinated bundles of services tailored to each subgroup, improving efficiency and person-centred care.',
  // C7 — Value-based payment models
  'Value-Based Remuneration': 'Compensating providers according to the outcomes they deliver and the total cost of the care episode, through mechanisms such as pay-for-performance, bundled/episode payments, and shared-savings contracts.',
  // C8 — Value-based procurement
  'Value-Based Purchasing Framework': 'Tying procurement decisions and payments to clinical outcomes and total cost-of-ownership, while apportioning risk and reward between vendors and providers.',
  // C9 — Integrated provider networks
  'Information Continuity': 'Ensuring critical clinical data (e-referrals, discharge summaries, structured records) flow in real time or near real time between organisations, so each clinician sees the complete patient story.',
  'Operational Integration': 'Joint care coordination across providers and levels of the health system, enabling health service provision along shared integrated care pathways and seamless care transitions for patients.',
  // C10 — Strategic change and innovation ecosystem
  'Innovation Ecosystem': 'Generating new and sustaining existing institutions, talent, and knowledge necessary to build and scale innovation throughout the health system.',
  'Investment and Knowledge Creation': 'Channeling funding and protecting intellectual property that enable design, adoption and diffusion of high-value innovations.',
  'Strategic Public-Private Partnerships': 'Mobilising cross-sector capabilities and aligning incentives to design, introduce, and scale innovations.'
};

// All component scores are already on a 0-5 scale
const max_scores_dict = {};
section_names.forEach(name => { max_scores_dict[name] = 5; });

// In the new data model, variable names in the CSV match section names directly
const ELEMENT_TO_VARIABLE = {};
section_names.forEach(name => { ELEMENT_TO_VARIABLE[name] = name; });
const VARIABLE_TO_ELEMENT = {};
section_names.forEach(name => { VARIABLE_TO_ELEMENT[name] = name; });

// Load data from CSV
const loadAllCountriesData = async () => {
  try {
    const response = await fetch('/Current/HVHS_Dataset.csv');
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const text = await response.text();
    const lines = text.split('\n');
    let headerLine = lines[0];
    if (headerLine.charCodeAt(0) === 0xFEFF) headerLine = headerLine.slice(1);
    const headers = headerLine.split(',').map(h => h.trim());
    const data = [];
    for (let i = 1; i < lines.length; i++) {
      if (!lines[i].trim()) continue;
      const values = [];
      let currentValue = '';
      let insideQuotes = false;
      for (let char of lines[i]) {
        if (char === '"') { insideQuotes = !insideQuotes; }
        else if (char === ',' && !insideQuotes) { values.push(currentValue.trim()); currentValue = ''; }
        else { currentValue += char; }
      }
      values.push(currentValue.trim());
      if (values.length === headers.length) {
        const row = {};
        headers.forEach((header, index) => { row[header] = values[index]; });
        data.push(row);
      }
    }
    console.log('HVHS data loaded:', data.length, 'rows');
    return data;
  } catch (error) {
    console.error('Error loading HVHS data:', error);
    throw error;
  }
};

// Load element scores CSV
const loadElementScores = async () => {
  try {
    const response = await fetch('/Current/HVHS_Element_Scores.csv');
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const text = await response.text();
    const lines = text.split('\n');
    let headerLine = lines[0];
    if (headerLine.charCodeAt(0) === 0xFEFF) headerLine = headerLine.slice(1);
    const headers = headerLine.split(',').map(h => h.trim());
    const data = [];
    for (let i = 1; i < lines.length; i++) {
      if (!lines[i].trim()) continue;
      const values = [];
      let currentValue = '';
      let insideQuotes = false;
      for (let char of lines[i]) {
        if (char === '"') { insideQuotes = !insideQuotes; }
        else if (char === ',' && !insideQuotes) { values.push(currentValue.trim()); currentValue = ''; }
        else { currentValue += char; }
      }
      values.push(currentValue.trim());
      if (values.length === headers.length) {
        const row = {};
        headers.forEach((header, index) => { row[header] = values[index]; });
        data.push(row);
      }
    }
    console.log('Element scores loaded:', data.length, 'rows');
    return data;
  } catch (error) {
    console.error('Error loading element scores:', error);
    return [];
  }
};

// Load per-variable metadata CSV (multi-line quoted fields → use Papa Parse)
const loadMetadata = async () => {
  try {
    const response = await fetch('/Current/HVHS_Metadata.csv');
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const text = await response.text();
    const parsed = Papa.parse(text, { header: true, skipEmptyLines: true });
    console.log('Metadata loaded:', parsed.data.length, 'rows');
    return parsed.data;
  } catch (error) {
    console.error('Error loading metadata:', error);
    return [];
  }
};

// All metadata rows for a given sub-element (matched by element name, case-insensitive)
const getMetadataForElement = (metadataData, elementName) => {
  if (!metadataData || !elementName) return [];
  const key = elementName.trim().toLowerCase();
  return metadataData.filter(r => (r.Element || '').trim().toLowerCase() === key);
};

// Get list of available countries
const getAvailableCountries = (data) => {
  return [...new Set(data.map(row => row.Country))].sort();
};

// Get available years
const getAvailableYears = (data) => {
  return [...new Set(data.map(row => row.Year))].sort();
};

const filterDataByCountryAndYear = (data, country, year) => {
  return data.filter(row => row.Country === country && row.Year === year);
};

const filterDataByCountry = (data, country) => {
  return data.filter(row => row.Country === country);
};

const calculateCountryScore = (countryData) => {
  if (!countryData || countryData.length === 0) return null;
  const overallRow = countryData.find(row => row.Variable === 'Overall');
  if (overallRow) {
    const val = parseFloat(overallRow.Value);
    if (!isNaN(val)) return val;
  }
  const componentRows = countryData.filter(row =>
    row.Variable !== 'Overall' && section_names.includes(row.Variable)
  );
  if (componentRows.length === 0) return null;
  const values = componentRows.map(r => parseFloat(r.Value)).filter(v => !isNaN(v));
  if (values.length === 0) return null;
  return values.reduce((a, b) => a + b, 0) / values.length;
};

const getAllCountryScores = (data) => {
  const countries = getAvailableCountries(data);
  const countryScores = {};
  countries.forEach(country => {
    const countryData = filterDataByCountry(data, country);
    const score = calculateCountryScore(countryData);
    if (score !== null) countryScores[country] = score;
  });
  return countryScores;
};

const parseFrameworkData = (countryData, year) => {
  const yearData = countryData.filter(row => row.Year === year);
  const scores = [];
  const elements = [];
  yearData.forEach(row => {
    const variable = row.Variable;
    const value = parseFloat(row.Value);
    if (!isNaN(value) && variable !== 'Overall' && section_names.includes(variable)) {
      scores.push(value);
      elements.push(variable);
    }
  });
  return { scores, elements };
};

const getAveragedNormalizedData = (scores, elements) => {
  const normalizedScores = [];
  section_names.forEach(sectionName => {
    const index = elements.indexOf(sectionName);
    if (index !== -1) {
      normalizedScores.push(scores[index]);
    } else {
      normalizedScores.push(0);
    }
  });
  return normalizedScores;
};

const getSectionData = (sectionName, parsedData) => {
  const { scores, elements } = parsedData;
  const index = elements.indexOf(sectionName);
  if (index !== -1) return { scores: [scores[index]], elements: [sectionName] };
  return { scores: [], elements: [] };
};

const getSectionDetailedData = (sectionName, countryData, year) => {
  const yearData = countryData.filter(row => row.Year === year);
  const sectionData = yearData.filter(row => row.Variable === sectionName);
  return sectionData.map(row => {
    const value = parseFloat(row.Value);
    return {
      element: sectionName,
      variable: row.Variable,
      value: value,
      maxScore: 5,
      normalizedScore: value,
      displayScore: value.toFixed(2),
      unitOfMeasure: row['Unit of Measure'] || 'Score (0-5)',
      year: row.Year,
      isError: isNaN(value)
    };
  });
};

// Get element scores for a specific country and component
const getElementScoresForComponent = (elementScoresData, country, componentIndex) => {
  const componentNum = componentIndex + 1;
  return elementScoresData
    .filter(row => row.Country === country && String(row.Component) === String(componentNum))
    .map(row => ({
      element: row.Element,
      score: row['Element Score'] === 'NA' ? null : parseFloat(row['Element Score']),
      definition: ELEMENT_DEFINITIONS[row.Element] || ''
    }));
};

// Get scores for a specific component across all countries
const getComponentScores = (data, componentName) => {
  const countries = getAvailableCountries(data);
  const countryToISO3 = {};
  data.forEach(row => {
    if (row.Country && row['ISO-Code']) countryToISO3[row.Country] = row['ISO-Code'];
  });
  const countryScores = {};
  countries.forEach(country => {
    const rows = data.filter(row => row.Country === country && row.Variable === componentName);
    if (rows.length > 0) {
      // Use the best (most recent) score available
      const vals = rows.map(r => parseFloat(r.Value)).filter(v => !isNaN(v));
      if (vals.length > 0) countryScores[country] = vals[vals.length - 1];
    }
  });
  return countryScores;
};

// Render interactive world map — scoreType: 'Overall' or a component name
const renderWorldMap = (container, allCountriesData, onCountryClick, scoreType) => {
  const displayName = scoreType || 'Overall';
  let countryScoresMap;

  if (!scoreType || scoreType === 'Overall') {
    countryScoresMap = getAllCountryScores(allCountriesData);
  } else {
    countryScoresMap = getComponentScores(allCountriesData, scoreType);
  }

  const countryToISO3 = {};
  allCountriesData.forEach(row => {
    if (row.Country && row['ISO-Code']) countryToISO3[row.Country] = row['ISO-Code'];
  });

  const allCountries = getAvailableCountries(allCountriesData)
    .map(country => ({
      country,
      code: countryToISO3[country] || country,
      score: countryScoresMap[country]
    }))
    .filter(d => d.code && d.code.length === 3);

  const validData = allCountries.filter(d => scoreToBandIndex(d.score) !== null);
  const missingData = allCountries.filter(d => scoreToBandIndex(d.score) === null);

  const hoverLabel = displayName === 'Overall' ? 'Overall Score' : displayName;
  const bandCount = HVHS_SCORE_BANDS.length;
  const hoverlabel = { bgcolor: '#1F2937', font: { color: 'white', size: 12 }, align: 'left' };
  const borderLine = { color: '#D6D6DA', width: 0.5 };

  const data = [{
    type: 'choropleth',
    name: hoverLabel,
    locations: validData.map(d => d.code),
    z: validData.map(d => scoreToBandIndex(d.score) + 0.5),
    text: validData.map(d => d.country),
    customdata: validData.map(d => d.score),
    hovertemplate: `<b>%{text}</b><br>${hoverLabel}: %{customdata:.1f} / 5.0<extra></extra>`,
    hoverlabel,
    colorscale: HVHS_BAND_COLORSCALE,
    autocolorscale: false,
    zmin: 0, zmax: bandCount,
    zauto: false,
    showscale: true,
    colorbar: {
      title: { text: 'Score (0–5)', side: 'right', font: { size: 11, color: '#6B7280' } },
      tickvals: HVHS_SCORE_BANDS.map((b, i) => i + 0.5),
      ticktext: HVHS_SCORE_BANDS.map(b => b.label),
      tickfont: { size: 11, color: '#1F2937' },
      ticks: '',
      ticklen: 0,
      x: 1.0,
      xanchor: 'left',
      xpad: 8,
      y: 0.56,
      yanchor: 'middle',
      len: 0.5,
      thickness: 14,
      outlinewidth: 0
    },
    marker: { line: borderLine }
  }];

  if (missingData.length > 0) {
    data.push({
      type: 'choropleth',
      name: HVHS_MISSING_LABEL,
      locations: missingData.map(d => d.code),
      z: missingData.map(() => 0),
      text: missingData.map(d => d.country),
      hovertemplate: `<b>%{text}</b><br>${hoverLabel}: N/A<extra></extra>`,
      hoverlabel,
      colorscale: [[0, HVHS_MISSING_COLOR], [1, HVHS_MISSING_COLOR]],
      autocolorscale: false,
      zmin: 0, zmax: 1,
      zauto: false,
      showscale: false,
      marker: { line: borderLine }
    });
  }

  const legendY = 0.56 - 0.25 - 0.07;
  const noDataLegend = {
    shapes: [{
      type: 'rect',
      xref: 'paper', yref: 'paper',
      xsizemode: 'pixel', ysizemode: 'pixel',
      xanchor: 1.0, yanchor: legendY,
      x0: 8, x1: 22, y0: -6, y1: 6,
      fillcolor: HVHS_MISSING_COLOR,
      line: { color: '#D6D6DA', width: 0.5 },
      layer: 'above'
    }],
    annotations: [{
      xref: 'paper', yref: 'paper',
      x: 1.0, y: legendY,
      xanchor: 'left', yanchor: 'middle',
      xshift: 28,
      text: HVHS_MISSING_LABEL,
      showarrow: false,
      font: { size: 11, color: '#1F2937' }
    }]
  };

  const layout = {
    ...noDataLegend,
    geo: {
      projection: { type: 'natural earth' },
      showframe: false,
      showcoastlines: true,
      coastlinecolor: '#D6D6DA',
      showland: true,
      landcolor: '#F5F5F5',
      showocean: true,
      oceancolor: '#1e3a5f',
      showlakes: true,
      lakecolor: '#2563eb',
      showcountries: true,
      countrycolor: '#D6D6DA',
      lataxis: { range: [-60, 90] }
    },
    paper_bgcolor: 'white', plot_bgcolor: 'white',
    margin: { t: 5, b: 5, l: 0, r: 90 }, height: 550, autosize: true
  };

  Plotly.newPlot(container, data, layout, { responsive: true, displayModeBar: false, displaylogo: false });
  container.on('plotly_click', function(data) {
    if (data.points && data.points.length > 0) {
      const clickedCountry = data.points[0].text;
      if (onCountryClick && clickedCountry) onCountryClick(clickedCountry);
    }
  });
};

// R7: Fixed distinct color per component (cycling through brand palette)
const COMPONENT_PALETTE = [
  '#abf4c3', // Seafoam (brightest)
  '#FFBF88', // Khaki
  '#ffefd8', // Beige
  '#4b1b1b', // Brick
  '#40305d', // Violet
  '#113743', // Bluegreen
];

const componentIndexToColor = (index) => {
  return COMPONENT_PALETTE[index % COMPONENT_PALETTE.length];
};

// Score → band colour (shared with the world map); missing → HVHS_MISSING_COLOR
const scoreToColor = (score) => {
  const idx = scoreToBandIndex(score);
  return idx === null ? HVHS_MISSING_COLOR : HVHS_SCORE_BANDS[idx].color;
};

// Wrap a "Cx: long name" label so Plotly tick text doesn't run off the edge.
// Per CR feedback 27 Apr — C3, C4, C9, C10 labels were getting cut off at the
// horizontal margins. We split the longest names onto a second line.
const wrapRadialLabel = (label) => {
  // label looks like "C10: Strategic change and innovation ecosystem"
  const m = label.match(/^(C\d+):\s*(.+)$/);
  if (!m) return label;
  const prefix = m[1];
  const rest = m[2];
  if (rest.length <= 18) return `${prefix}: ${rest}`;
  // Find a break point near the middle on a space boundary
  const target = Math.floor(rest.length / 2);
  let breakAt = rest.indexOf(' ', target);
  if (breakAt === -1) breakAt = rest.lastIndexOf(' ', target);
  if (breakAt === -1) return `${prefix}: ${rest}`;
  return `${prefix}: ${rest.slice(0, breakAt)}<br>${rest.slice(breakAt + 1)}`;
};

// Render radial BAR chart with full component names and hover callback
const renderRadialChart = (container, normalizedScores, sectionNames, onHoverComponent) => {
  // R4: Filter out components with no data — only show components with real scores
  const filteredIndices = [];
  const filteredLabels = [];
  const filteredScores = [];
  const filteredColors = [];

  sectionNames.forEach((name, i) => {
    const s = normalizedScores[i];
    if (s !== 0 && s !== null && s !== undefined) {
      filteredIndices.push(i);
      filteredLabels.push(wrapRadialLabel(`C${i + 1}: ${name}`));
      filteredScores.push(s);
      filteredColors.push(scoreToColor(s));
    }
  });

  const data = [{
    type: 'barpolar',
    r: filteredScores,
    theta: filteredLabels,
    name: 'Score',
    marker: {
      color: filteredColors,
      line: { color: 'rgba(80,60,120,0.4)', width: 1 }
    },
    hovertemplate: filteredLabels.map((l, i) =>
      `<b>${l}</b><br>Score: ${filteredScores[i].toFixed(2)} / 5.0<extra></extra>`
    ),
    showlegend: false
  }];

  const containerHeight = container.offsetHeight || 500;
  const containerWidth = container.offsetWidth || 500;

  // R3 / CR 27 Apr: generous horizontal margins + label wrapping above so
  // C3/C4/C9/C10 labels don't get clipped at the plot edges.
  const layout = {
    polar: {
      radialaxis: {
        visible: true,
        range: [0, 5],
        tickvals: [0, 1, 2, 3, 4, 5],
        ticktext: ['0', '1', '2', '3', '4', '5'],
        tickfont: { size: 9, color: '#9CA3AF' },
        gridcolor: '#D1D5DB',
        linecolor: '#D1D5DB',
        showline: true
      },
      angularaxis: {
        tickfont: { size: 9, color: '#374151' },
        direction: 'clockwise',
        rotation: 90,
        gridcolor: '#D1D5DB'
      },
      bgcolor: 'white',
      bargap: 0
    },
    showlegend: false,
    paper_bgcolor: 'white',
    plot_bgcolor: 'white',
    margin: { t: 90, b: 90, l: 180, r: 180 },
    height: containerHeight,
    width: containerWidth,
    autosize: false
  };

  Plotly.newPlot(container, data, layout, { responsive: true, displayModeBar: false, displaylogo: false });

  // Hover event -> triggers right panel update
  container.on('plotly_hover', function(hoverData) {
    if (hoverData.points && hoverData.points.length > 0) {
      const label = hoverData.points[0].theta;
      const match = label.match(/^C(\d+):/);
      if (match) {
        const idx = parseInt(match[1]) - 1;
        const section = sectionNames[idx];
        if (onHoverComponent) onHoverComponent(idx, section);
      }
    }
  });

  // Click event for sections — lock the hovered component
  container.on('plotly_click', function(clickData) {
    if (clickData.points && clickData.points.length > 0) {
      const label = clickData.points[0].theta;
      const match = label.match(/^C(\d+):/);
      if (match) {
        const idx = parseInt(match[1]) - 1;
        const section = sectionNames[idx];
        // Dispatch lock event so the right panel stays on this component
        const event = new CustomEvent('hvhsSectionClick', { detail: { index: idx, section } });
        document.dispatchEvent(event);
      }
    }
  });
};

// Render element bar chart for a component (vertical bars: x = elements, y = score)
const renderElementBarChart = (container, elementScores, componentName) => {
  if (!container) return;

  const elements = elementScores.filter(e => e.score !== null && !isNaN(e.score));
  const naElements = elementScores.filter(e => e.score === null || isNaN(e.score));
  const allElements = [...elements, ...naElements];

  const barColors = allElements.map(e =>
    e.score !== null && !isNaN(e.score)
      ? scoreToColor(e.score)
      : HVHS_MISSING_COLOR
  );

  const data = [{
    type: 'bar',
    x: allElements.map(e => e.element),
    y: allElements.map(e => e.score !== null && !isNaN(e.score) ? e.score : 0),
    text: allElements.map(e => e.score !== null && !isNaN(e.score) ? e.score.toFixed(2) : 'N/A'),
    textposition: 'outside',
    textfont: { size: 12, color: '#374151' },
    marker: {
      color: barColors,
      line: { color: 'rgba(80,60,120,0.3)', width: 1 }
    },
    hovertemplate: allElements.map(e =>
      `<b>${e.element}</b><br>${e.definition}<br>Score: ${e.score !== null && !isNaN(e.score) ? e.score.toFixed(2) : 'N/A'} / 5.0<extra></extra>`
    ),
    hoverlabel: {
      bgcolor: '#1F2937',
      font: { color: 'white', size: 12 },
      align: 'left'
    }
  }];

  const layout = {
    xaxis: {
      automargin: true,
      tickfont: { size: 10, color: '#374151' },
      tickangle: -30
    },
    yaxis: {
      range: [0, 5.5],
      tickvals: [0, 1, 2, 3, 4, 5],
      tickfont: { size: 11, color: '#6B7280' },
      gridcolor: '#E5E7EB',
      title: { text: 'Score (0–5)', font: { size: 11, color: '#6B7280' } }
    },
    paper_bgcolor: 'white',
    plot_bgcolor: '#FAFAFA',
    margin: { t: 10, b: 10, l: 50, r: 20 },
    height: 260,
    bargap: 0.3
  };

  Plotly.newPlot(container, data, layout, { responsive: true, displayModeBar: false, displaylogo: false });
};

// Render distribution/comparison chart
const renderDistributionChart = (container, sectionName, countryData, year) => {
  const variable = ELEMENT_TO_VARIABLE[sectionName];
  const yearData = countryData.filter(row => row.Year === year && row.Variable === variable);
  if (yearData.length === 0) return;
  const row = yearData[0];
  const value = parseFloat(row.Value);
  const maxScore = max_scores_dict[variable] || 1;
  const normalizedScore = (value / maxScore) * 5;

  const data = [{
    type: 'bar', x: [sectionName], y: [normalizedScore],
    text: [`Raw: ${value}${maxScore === 100 ? '%' : ''}`], textposition: 'outside',
    marker: { color: scoreToColor(normalizedScore), line: { color: '#374151', width: 1 } },
    hovertemplate: `<b>${sectionName}</b><br>Normalized: %{y:.2f} / 5.0<extra></extra>`
  }];

  Plotly.newPlot(container, data, {
    xaxis: { title: '', showticklabels: false, showgrid: false },
    yaxis: { title: 'Normalized Score', range: [0, 5.5], tickvals: [0,1,2,3,4,5] },
    paper_bgcolor: 'white', plot_bgcolor: '#f9fafb',
    margin: { t: 60, b: 40, l: 60, r: 40 }, height: 400
  }, { responsive: true, displayModeBar: false, displaylogo: false });
};

// Render time series chart
const renderTimeSeriesChart = (container, countryData, sectionName = null) => {
  const years = getAvailableYears(countryData.map ? countryData : []).sort();
  const sectionsToShow = sectionName ? [sectionName] : section_names;
  const colors = {
    'Digital Data Systems': '#3B82F6', 'Analytics': '#10B981',
    'Cost measurement systems': '#F59E0B', 'Outcome measurement systems': '#EF4444',
    'Benchmarking': '#8B5CF6', 'Integrated Care Pathways': '#EC4899',
    'Value-Based Payment': '#06B6D4', 'Value-based procurement': '#F97316',
    'Integrated Provider Networks': '#14B8A6', 'Strategic change and innovation ecosystem': '#6366F1'
  };
  const traces = [];
  sectionsToShow.forEach(section => {
    const yearValues = years.map(year => {
      const yearData = countryData.filter(row => row.Year === year && row.Variable === section);
      if (yearData.length > 0) { const v = parseFloat(yearData[0].Value); return isNaN(v) ? null : v; }
      return null;
    });
    traces.push({
      x: years, y: yearValues, mode: 'lines+markers', name: section,
      line: { color: colors[section], width: 3 }, marker: { size: 8 },
      hovertemplate: `<b>${section}</b><br>Year: %{x}<br>Score: %{y:.2f} / 5.0<extra></extra>`
    });
  });
  Plotly.newPlot(container, traces, {
    xaxis: { title: 'Year', tickmode: 'array', tickvals: years },
    yaxis: { title: 'Normalized Score', range: [0, 5.5], tickvals: [0,1,2,3,4,5] },
    showlegend: !sectionName,
    legend: { x: 1.02, y: 1, xanchor: 'left' },
    paper_bgcolor: 'white', plot_bgcolor: '#f9fafb',
    margin: { t: 20, b: 60, l: 60, r: sectionName ? 40 : 180 }, height: 400
  }, { responsive: true, displayModeBar: false, displaylogo: false });
};

export default {
  section_names,
  section_short_labels,
  HVHS_SCORE_BANDS,
  HVHS_MISSING_COLOR,
  HVHS_MISSING_LABEL,
  scoreToBandIndex,
  scoreToColor,
  ELEMENT_DEFINITIONS,
  COMPONENT_DESCRIPTIONS,
  ELEMENT_TO_VARIABLE,
  VARIABLE_TO_ELEMENT,
  max_scores_dict,
  loadAllCountriesData,
  loadElementScores,
  loadMetadata,
  getMetadataForElement,
  getAvailableCountries,
  getAvailableYears,
  filterDataByCountry,
  filterDataByCountryAndYear,
  calculateCountryScore,
  getAllCountryScores,
  parseFrameworkData,
  getAveragedNormalizedData,
  getSectionData,
  getSectionDetailedData,
  getElementScoresForComponent,
  renderWorldMap,
  renderRadialChart,
  renderElementBarChart,
  renderDistributionChart,
  renderTimeSeriesChart
};